/**
 * All the arithmetic and formatting for "YYYY-MM-DD" day strings, in one
 * place, done in UTC so a day string that already carries the viewer's zone
 * is never shifted a second time.
 *
 * A "YYYY-MM-DD" day string coming back from events-api is already in the
 * viewer's zone -- the server rendered it there. Formatting it again in the
 * viewer's zone shifts it by a day for anyone west of UTC. Every formatter
 * here therefore runs on Date.UTC(year, month - 1, day) with timeZone: "UTC".
 * `todayInZone` is the ONLY function that reads the viewer's zone: it turns
 * an instant into a day.
 */
export {
  dayChipTopLabel,
  dayOfMonth,
  isToday,
  longDayLabel,
  monthShort,
  shortDayLabel,
  weekdayShort,
} from "@sportsmet/theme/scores";

const DAY_STRING_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function parseDay(day: string): {
  year: number;
  month: number;
  dayOfMonth: number;
} {
  const [yearPart, monthPart, dayPart] = day.split("-");
  return {
    year: Number(yearPart),
    month: Number(monthPart),
    dayOfMonth: Number(dayPart),
  };
}

function toDayString(year: number, month: number, dayOfMonth: number): string {
  return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(dayOfMonth)}`;
}

/** True when `day` matches /^\d{4}-\d{2}-\d{2}$/ and names a real date. */
export function isDayString(value: string): boolean {
  if (!DAY_STRING_PATTERN.test(value)) return false;
  const { year, month, dayOfMonth } = parseDay(value);
  if (month < 1 || month > 12) return false;
  const date = new Date(Date.UTC(year, month - 1, dayOfMonth));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === dayOfMonth
  );
}

/** Today's calendar day in `timeZone`, "YYYY-MM-DD". The ONLY function here
 *  that uses the viewer's zone: it turns an instant into a day. */
export function todayInZone(input: {
  nowMs: number;
  timeZone: string;
}): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: input.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(input.nowMs));

  const year = parts.find((part) => part.type === "year")?.value ?? "1970";
  const month = parts.find((part) => part.type === "month")?.value ?? "01";
  const day = parts.find((part) => part.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}

/** Shift a "YYYY-MM-DD" by whole days. Pure UTC arithmetic. */
export function shiftDay(input: { day: string; deltaDays: number }): string {
  const { year, month, dayOfMonth } = parseDay(input.day);
  const date = new Date(
    Date.UTC(year, month - 1, dayOfMonth + input.deltaDays),
  );
  return toDayString(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

/** Shift a "YYYY-MM-DD" by whole months, clamping the day-of-month to the
 *  target month's length (Jan 31 minus one month is Feb 28 or 29). */
export function shiftMonth(input: {
  day: string;
  deltaMonths: number;
}): string {
  const { year, month, dayOfMonth } = parseDay(input.day);
  const targetMonthIndex = month - 1 + input.deltaMonths;
  // The first of the target month, in UTC. Date.UTC normalizes a month index
  // outside 0-11 into the correct year, so this handles a year boundary too.
  const firstOfTargetMonth = new Date(Date.UTC(year, targetMonthIndex, 1));
  const targetYear = firstOfTargetMonth.getUTCFullYear();
  const targetMonth = firstOfTargetMonth.getUTCMonth();
  // Day 0 of the NEXT month is the last day of the target month.
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0),
  ).getUTCDate();
  const clampedDay = Math.min(dayOfMonth, lastDayOfTargetMonth);
  return toDayString(targetYear, targetMonth + 1, clampedDay);
}

// The fan-facing day words ("Mon", "Monday, Oct 19") live in
// `@sportsmet/theme/scores` (`dayLabels.ts`), Intl free and shared with
// mobile, and are re-exported above so every web caller keeps this import.

/** "September 2026" -- the month calendar's heading. */
export function monthLabel(day: string): string {
  const { year, month, dayOfMonth: dom } = parseDay(day);
  const date = new Date(Date.UTC(year, month - 1, dom));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** The first day of `day`'s month, "YYYY-MM-01". */
export function firstDayOfMonth(day: string): string {
  const { year, month } = parseDay(day);
  return toDayString(year, month, 1);
}

/** The last day of `day`'s month. */
export function lastDayOfMonth(day: string): string {
  const { year, month } = parseDay(day);
  // Day 0 of the next month is the last day of this one.
  const date = new Date(Date.UTC(year, month, 0));
  return toDayString(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}
